/**
 * Authentication Context
 * Manages user authentication state and provides auth methods
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { authApi } from '../api/auth';
import { usersApi } from '../api/users';
import { User, ApiError } from '../api/types';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isModerator: boolean;
  isPartner: boolean;
  login: (email: string, password: string, twoFactorCode?: string, recoveryCode?: string) => Promise<void | { requires_2fa_setup: boolean; user: User }>;
  loginWithOtp: (identifier: string, otp: string, type: 'email' | 'phone') => Promise<void | { requires_2fa_setup: boolean; user: User }>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<User | undefined>;
  sendOtp: (identifier: string, type: 'email' | 'phone') => Promise<void>;
}

interface RegisterData {
  name: string;
  email: string;
  phone?: string;
  phone_country_code?: string;
  password: string;
  password_confirmation: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasRefreshedUserOnMount = useRef(false);

  // Helper: resolve role from roles array or direct role field (API may return either)
  const hasRole = (name: string): boolean => {
    if (!user) return false;
    if (user.roles?.some((r) => r.name === name)) return true;
    const direct = (user as { role?: string }).role;
    return direct === name || (direct === 'user' && name === 'youth') || (name === 'user' && direct === 'youth');
  };

  // Check if user has super_admin role
  const isSuperAdmin = hasRole('super_admin');

  // Check if user has admin or super_admin role
  const isAdmin = hasRole('admin') || isSuperAdmin;

  // Check if user has moderator role
  const isModerator = hasRole('moderator');

  // Check if user has partner role
  const isPartner = hasRole('partner');

  // Load user from localStorage on mount
  useEffect(() => {
    const loadStoredAuth = () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        }
      } catch (error) {
        console.error('Error loading stored auth:', error);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      } finally {
        setIsLoading(false);
      }
    };

    loadStoredAuth();
  }, []);

  // Logout function (defined early for use in refreshUser)
  const logout = useCallback(async () => {
    try {
      if (token) {
        await authApi.logout();
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      // Use window.location for navigation since we're outside Router context
      window.location.href = '/login';
    }
  }, [token]);

  // Refresh user data from API
  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getCurrentUser();
      if (response.data?.user) {
        const userData = response.data.user;
        setUser(userData);
        localStorage.setItem('auth_user', JSON.stringify(userData));
        return userData; // Return user data for components that need it
      }
    } catch (error: any) {
      // Ignore aborted requests (they're expected during cleanup or navigation)
      if (
        error?.code === 'ECONNABORTED' ||
        error?.name === 'AbortError' ||
        error?.message === 'Request aborted' ||
        error?.message?.includes('aborted') ||
        error?.isAborted
      ) {
        return; // Silently ignore aborted requests
      }
      // Only log and handle non-aborted errors
      console.error('Error refreshing user:', error);
      // If refresh fails, user might be logged out
      logout();
      throw error;
    }
  }, [logout]);

  // Refresh user when token exists and we have no user (e.g. after page reload with token only)
  useEffect(() => {
    if (token && !user) {
      refreshUser();
    }
  }, [token, user, refreshUser]);

  // When we have token + user from localStorage, refresh once so roles/menu are up to date (fixes super_admin seeing youth menu)
  useEffect(() => {
    if (!token || !user || hasRefreshedUserOnMount.current) return;
    hasRefreshedUserOnMount.current = true;
    refreshUser().catch(() => { /* ignore; logout or periodic refresh will handle */ });
  }, [token, user, refreshUser]);

  // Refresh user data periodically to ensure role changes are reflected
  useEffect(() => {
    if (token && user) {
      // Refresh user data every 5 minutes to catch role changes
      const interval = setInterval(() => {
        refreshUser();
      }, 5 * 60 * 1000); // 5 minutes

      return () => clearInterval(interval);
    }
  }, [token, user, refreshUser]);

  // Login with email and password
  const login = useCallback(async (identifier: string, password: string, twoFactorCode?: string, recoveryCode?: string) => {
    try {
      const loginData: any = { identifier, password };
      if (twoFactorCode) {
        loginData.two_factor_code = twoFactorCode;
      }
      if (recoveryCode) {
        loginData.recovery_code = recoveryCode;
      }

      const response = await authApi.login(loginData);
      
      // Check if 2FA is required (backend returns 200 with requires_2fa flag)
      if ((response as any).requires_2fa === true) {
        const error: any = new Error('Two-factor authentication required');
        error.requires_2fa = true;
        error.method = (response as any).method;
        throw error;
      }
      
      // Handle different response formats
      // Backend returns: { message, user, token } directly
      // Or wrapped: { success: true, data: { user, token } }
      let userData: User;
      let authToken: string;

      // Check if response has data wrapper
      if (response.data && response.data.user && response.data.token) {
        // Format: { success: true, data: { user, token } }
        userData = response.data.user;
        authToken = response.data.token;
      } else if ((response as any).user && (response as any).token) {
        // Format: { user, token, message } - direct from backend
        userData = (response as any).user;
        authToken = (response as any).token;
      } else {
        throw new Error('Invalid response format');
      }

      setUser(userData);
      setToken(authToken);
      localStorage.setItem('auth_token', authToken);
      localStorage.setItem('auth_user', JSON.stringify(userData));

      const requires2faSetup = !!(response as any).requires_2fa_setup;
      if (requires2faSetup) {
        return { requires_2fa_setup: true, user: userData };
      }
    } catch (error) {
      const apiError = error as ApiError;
      // Preserve the original ApiError to maintain requires_verification and requires_2fa flags
      if (apiError && typeof apiError === 'object') {
        if ('requires_verification' in apiError || 'requires_2fa' in apiError) {
          throw apiError; // Throw original error to preserve all properties
        }
      }
      throw new Error(apiError?.message || 'Login failed');
    }
  }, []);

  // Login with OTP
  const loginWithOtp = useCallback(async (identifier: string, otp: string, type: 'email' | 'phone') => {
    try {
      const response = await authApi.verifyOtp(identifier, otp, type);
      
      // authApi.verifyOtp returns response.data (the backend JSON response)
      // Backend returns: { message, user, token? } directly (not wrapped in ApiResponse format)
      let userData: User;
      let authToken: string | undefined;

      // Handle different response formats (backend sometimes wraps, sometimes doesn't)
      if ((response as any).data && (response as any).data.user) {
        // Format: { data: { user, token? }, message }
        userData = (response as any).data.user;
        authToken = (response as any).data.token;
      } else if ((response as any).user) {
        // Format: { user, token?, message } - direct from backend (most common)
        userData = (response as any).user;
        authToken = (response as any).token;
      } else {
        throw new Error('Invalid response format: user data not found');
      }

      if (!userData) {
        throw new Error('User data not found in response');
      }

      // Only update auth state if token is provided (email verification during login)
      if (authToken) {
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));

        const requires2faSetup = !!(response as any).requires_2fa_setup;
        if (requires2faSetup) {
          return { requires_2fa_setup: true, user: userData };
        }
      } else {
        // For phone verification without login, just update user without token
        setUser(userData);
        localStorage.setItem('auth_user', JSON.stringify(userData));
      }
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || 'OTP verification failed');
    }
  }, []);

  // Register new user
  const register = useCallback(async (data: RegisterData) => {
    try {
      const response = await authApi.register(data);
      
      // Registration might return user without token if verification is required
      if (response.data?.user && response.data?.token) {
        const userData = response.data.user;
        const authToken = response.data.token;
        
        setUser(userData);
        setToken(authToken);
        localStorage.setItem('auth_token', authToken);
        localStorage.setItem('auth_user', JSON.stringify(userData));
      } else if (response.data?.user) {
        // User created but needs verification
        setUser(response.data.user);
        localStorage.setItem('auth_user', JSON.stringify(response.data.user));
      }
    } catch (error) {
      const apiError = error as ApiError;
      // Preserve the full error object including validation errors
      const errorToThrow: any = new Error(apiError.message || 'Registration failed');
      errorToThrow.errors = apiError.errors;
      throw errorToThrow;
    }
  }, []);


  // Send OTP
  const sendOtp = useCallback(async (identifier: string, type: 'email' | 'phone') => {
    try {
      await authApi.sendOtp(identifier, type);
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || 'Failed to send OTP');
    }
  }, []);

  // Global activity heartbeat - update user activity every 30 seconds when authenticated
  useEffect(() => {
    // Calculate authentication status directly
    const authenticated = !!user && !!token;
    if (!authenticated || !token) return;

    // Update activity immediately on mount
    const updateActivity = async () => {
      try {
        await usersApi.updateActivity();
      } catch (error) {
        // Silently fail - activity tracking is not critical
        if (import.meta.env.DEV) {
          console.debug('Failed to update activity:', error);
        }
      }
    };

    // Update immediately
    updateActivity();

    // Then update every 30 seconds
    const interval = setInterval(updateActivity, 30000);

    return () => clearInterval(interval);
  }, [user, token]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    isAdmin,
    isSuperAdmin,
    isModerator,
    isPartner,
    login,
    loginWithOtp,
    register,
    logout,
    refreshUser,
    sendOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

